import { invoke } from "@tauri-apps/api/core";
import { useEffect, useState } from "react";

import { env, useDocumentFromCurrentRoute, useEnvironments } from "@moment/state";

interface GitHubUser {
	login: string;
	avatar_url: string;
	html_url: string;
}

interface GitHubPullRequest {
	title: string;
	state: "open" | "closed";
	merged: boolean;
	user: GitHubUser;
	created_at: string;
	updated_at: string;
}

interface PullRequestDataUser {
	login: string;
	avatarUrl: string;
	htmlUrl: string;
}

interface PullRequestData {
	title: string;
	state: "open" | "closed" | "merged" | "unknown";
	author: PullRequestDataUser | null;
	isLoading: boolean;
	error: string | null;
}

type EnvFetchResponse = {
	status: number;
	body: string | null;
	status_text: string;
};

const fetchPullRequest = async (
	owner: string,
	repo: string,
	prNumber: number,
	options?: {
		environmentName: string | null;
		documentId: string | null;
	}
) => {
	const url = `https://api.github.com/repos/${owner}/${repo}/pulls/${prNumber}`;

	if (env.isDesktopApp) {
		const response = await invoke<EnvFetchResponse>("execute_env_fetch", {
			input: {
				request: {
					method: "GET",
					url,
					params: [],
					headers: [
						{ key: "Accept", value: "application/vnd.github+json" },
						{ key: "X-GitHub-Api-Version", value: "2022-11-28" },
					],
					auth: { type: "none" },
					body: { mode: "none", text: "", form: [] },
					timeoutMs: 30000,
					followRedirects: true,
					validateSSL: true,
				},
				env_vars: {},
				environment_name: options?.environmentName ?? null,
				document_id: options?.documentId ?? null,
			},
		});

		if (response.status < 200 || response.status >= 300 || !response.body) {
			throw new Error(`Failed to fetch PR data: ${response.status}`);
		}

		return JSON.parse(response.body) as GitHubPullRequest;
	}

	const response = await fetch(url, {
		headers: {
			"Accept": "application/vnd.github+json",
			"X-GitHub-Api-Version": "2022-11-28",
		},
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch PR data: ${response.status}`);
	}

	return (await response.json()) as GitHubPullRequest;
};

export const usePullRequestData = (owner: string, repo: string, prNumber: number) => {
	const { documentId } = useDocumentFromCurrentRoute();
	const { selectedEnvironmentName } = useEnvironments();

	const [data, setData] = useState<PullRequestData>({
		title: "",
		state: "open",
		author: null,
		isLoading: true,
		error: null,
	});

	useEffect(() => {
		if (!owner || !repo || !prNumber) {
			setData({
				title: "",
				state: "open",
				author: null,
				isLoading: false,
				error: null,
			});
			return;
		}

		const fetchPRData = async () => {
			try {
				setData((prev) => ({ ...prev, isLoading: true, error: null }));
				const prData = await fetchPullRequest(owner, repo, prNumber, {
					environmentName: selectedEnvironmentName,
					documentId,
				});

				const state = prData.merged ? "merged" : prData.state;

				const author: PullRequestDataUser | null = prData.user
					? {
							login: prData.user.login,
							avatarUrl: prData.user.avatar_url,
							htmlUrl: prData.user.html_url,
						}
					: null;

				setData({
					title: prData.title,
					state,
					author,
					isLoading: false,
					error: null,
				});
			} catch (error) {
				setData({
					title: "",
					state: "unknown",
					author: null,
					isLoading: false,
					error: error instanceof Error ? error.message : "Failed to fetch PR data",
				});
			}
		};

		fetchPRData();
	}, [documentId, owner, prNumber, repo, selectedEnvironmentName]);

	return data;
};
